<template>
    <article class="c-navigation relative z-0">
        <div ref="megaMenuAnchor" v-syncBodylockGap class="c-navigation-anchor relative z-1 bg-blue-500 oprema:bg-opremayellow-500 text-white oprema:text-black"
             :style="navigationStyle"
             @mouseleave="toggleMegaMenu(false)"
             @mouseenter="cancelMegaMenuClosing()">
            <div class="o-chrome-container">
                <section id="main-navigation"
                         v-skip-target="{ id: 'main-navigation', label: $dictionary.get('Accessibility.SkipLinks.MainNavigation.Label'), order: 2 }"
                         role="navigation"
                         :aria-label="$dictionary.get('Accessibility.SkipLinks.MainNavigation')"
                         class="level-1-menu">
                    <MainNavigationSlider :menus="menus"
                                          :active-menu="activeMenu"
                                          :mega-menu-shown="megaMenuShown"
                                          @activeMenuChanged="changeMegaMenuDebounce"
                                          @cancelMegaMenuNavigation="cancelMegaMenuNavigation"
                                          @closeMegaMenu="toggleMegaMenu(false)"/>
                </section>
            </div>
        </div>
        <transition name="mega-menu">
            <div v-if="megaMenuShown" ref="megaMenuContainer" class="mega-menu bg-white overflow-y-auto relative"
                 :style="megaMenuStyle()"
                 @mouseleave="toggleMegaMenu(false)"
                 @mouseenter="toggleMegaMenu(true)">
                <div class="o-chrome-container bg-white">
                    <section v-if="activeMenu" class="py-20 flex flex-row flex-wrap">
                        <div v-for="(menu, mi) in activeMenu.children" :key="mi" class="sm:w-1/4 xl:w-1/5 mb-20 pr-20">
                            <CultureAwareRouterLink :to="menu.url" class="sm:text-12 md:text-14 lg:text-16 font-semi-bold mb-10 hover:underline block truncate"
                                                    @click="toggleMegaMenu(false)">
                                <span :title="menu.name">{{ menu.name }}</span>
                            </CultureAwareRouterLink>
                            <ReadMore v-if="menu.children && menu.children.length > 0" :key="menu.name" tag="ul"
                                      :min-items="12"
                                      :animation-time-mod="0"
                                      button-class="text-gray-900 sm:text-10 md:text-12 lg:text-13 cursor-pointer hover:font-medium pt-6">
                                <li v-for="(child, ci) in menu.children" :key="ci">
                                    <CultureAwareRouterLink :to="child.url" class="leading-double sm:text-10 md:text-12 lg:text-13 font-regular hover:underline block truncate"
                                                            @click="toggleMegaMenu(false)">
                                        <span :title="child.name">{{ child.name }}</span>
                                    </CultureAwareRouterLink>
                                </li>
                                <template #collapse>
                                    {{ $dictionary.get('Generic.ReadLess') }}
                                </template>
                                <template #expand>
                                    {{ $dictionary.get('Generic.ReadMore') }}
                                </template>
                            </ReadMore>
                        </div>
                    </section>
                </div>
                <button type="button"
                        class="overlay__close"
                        :aria-label="$dictionary.get('Generic.Close')"
                        @click="close()">
                    <CIcon name="close" class="close-cross" height="12" width="12"/>
                </button>
            </div>
        </transition>
    </article>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, StyleValue, nextTick } from 'vue';
import megaMenuService from '@/project/shared/megaMenu.service';
import { MenuItem } from '@/types/apiServerContract';
import MainNavigationSlider from './MainNavigationSlider.vue';
import scrollService from '@/core/scroll/scroll.service';
import escEventService from '@/core/esc-event.service';
import debounce from 'lodash-es/debounce';
import themeService from '@/project/app/themes/theme.service';

const SHOW_DELAY = 50;
const CLOSE_DELAY = 100;
const CHANGE_DELAY = 350;

const emit = defineEmits<{
    (e: 'megaMenuShown', value: boolean): void;
}>();

const megaMenuShown = ref(false);
const menus = ref<MenuItem[] | null>([]);
const activeMenu = ref<MenuItem | null>(null);
const megaMenuContainer = ref<HTMLElement>();
const megaMenuAnchor = ref<HTMLElement>();

const navigationStyle = computed((): StyleValue => ({
    ...(themeService.theme?.menuColor && { backgroundColor: themeService.theme.menuColor }),
    ...(themeService.theme?.menuColor && { color: themeService.getColor(themeService.theme.menuColor) })
}));

const open = () => {
    if (megaMenuShown.value) return;

    megaMenuShown.value = true;
    emit('megaMenuShown', true);

    nextTick(() => {
        if (megaMenuContainer.value) {
            scrollService.disableBodyScroll(megaMenuContainer.value);
        }
    });

    escEventService.register(close);
};

const close = () => {
    if (!megaMenuShown.value) return;

    if (megaMenuContainer.value) {
        scrollService.enableBodyScroll(megaMenuContainer.value);
    }

    megaMenuShown.value = false;
    emit('megaMenuShown', false);

    escEventService.unregister(close);
};

const openMegaMenuDebounce = debounce(open, SHOW_DELAY);
const closeMegaMenuDebounce = debounce(close, CLOSE_DELAY);

const changeMegaMenu = (menu: MenuItem) => {
    activeMenu.value = menu;

    if (!megaMenuShown.value) {
        open();
    }
};

const changeMegaMenuDebounce = debounce(changeMegaMenu, CHANGE_DELAY);

const cancelMegaMenuNavigation = () => {
    changeMegaMenuDebounce.cancel();
};

const cancelMegaMenuClosing = () => {
    closeMegaMenuDebounce.cancel();
};

const toggleMegaMenu = (show: boolean) => {
    if (show) {
        closeMegaMenuDebounce.cancel();
        openMegaMenuDebounce();
    } else {
        openMegaMenuDebounce.cancel();
        closeMegaMenuDebounce();
    }
};

const megaMenuStyle = (): StyleValue => {
    const top = megaMenuAnchor.value?.getBoundingClientRect().bottom ?? 0;
    return {
        maxHeight: `calc(100vh - ${top}px)`
    };
};

const getMegaMenuData = async() => {
    menus.value = await megaMenuService.getMegaMenu();
};

onMounted(() => {
    getMegaMenuData();
});
</script>

<style lang="less" scoped>
.mega-menu {
    @apply border-blue-500 oprema:border-black hesa:border-hesared-500;
    border-bottom-width: 4px;

    .top-header-fixed & {
        max-height: calc(100vh - 13.4rem);
    }
}

.level-1-menu {
    height: 6rem;
}

.mega-menu-enter-active {
    transition: transform 250ms ease-out;
}

.mega-menu-leave-active {
    transition: transform 200ms ease-in;
}

.mega-menu-enter,
.mega-menu-leave-to {
    transform: translate3d(0, -100%, 0);
}

.overlay__close {
    position: absolute;
    top: 20px;
    right: 20px;
}
</style>
